<template>
  <section
    v-if="slides.length"
    class="hero container"
  >
    <div class="hero__stage">
      <transition
        name="slidefade"
        mode="out-in"
      >
        <component
          :is="slide.href ? 'a' : 'div'"
          :key="index"
          class="hero__slide"
          :href="slide.href || null"
          @click="slide.href && slide.href.startsWith('#') ? goTo(slide.href, $event) : null"
        >
          <!-- visuel fourni par le graphiste : texte inclus dans l'image.
               Deux formats : paysage en desktop, portrait en mobile. -->
          <picture>
            <source
              media="(max-width: 767px)"
              :srcset="slide.imageMobile || slide.imageDesktop"
            >
            <img
              :src="slide.imageDesktop"
              :alt="slide.alt"
            >
          </picture>
        </component>
      </transition>

      <template v-if="slides.length > 1">
        <button
          class="hero__arrow is-prev"
          aria-label="Slide précédente"
          @click="step(-1)"
        >
          <img
            src="/assets/pictos/ui/slider-arrow.svg"
            alt=""
          >
        </button>
        <button
          class="hero__arrow is-next"
          aria-label="Slide suivante"
          @click="step(1)"
        >
          <img
            src="/assets/pictos/ui/slider-arrow.svg"
            alt=""
          >
        </button>
      </template>
    </div>
  </section>
</template>

<script>
import { scheduled } from '@/content/ccContent'
import { scrollToAnchor } from '@/utils/scroll'

export default {
  name: 'HeroSlider',
  props: {
    data: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      index: 0,
      timer: null
    }
  },
  computed: {
    // seulement les slides en cours de diffusion, les permanentes en dernier
    slides() {
      return scheduled(this.data.hero.slides)
    },
    slide() {
      return this.slides[this.index] || this.slides[0]
    }
  },
  watch: {
    // si le contenu change (arrivée de Strapi), l'index peut dépasser
    slides(list) {
      if (this.index >= list.length) this.index = 0
    }
  },
  mounted() {
    this.autoplay()
  },
  beforeUnmount() {
    window.clearInterval(this.timer)
  },
  methods: {
    autoplay() {
      window.clearInterval(this.timer)
      if (this.slides.length > 1) {
        this.timer = window.setInterval(() => this.step(1, false), 7000)
      }
    },
    step(dir, resetTimer = true) {
      const n = this.slides.length
      if (!n) return
      this.index = (this.index + dir + n) % n
      if (resetTimer) this.autoplay()
    },
    goTo(anchor, event) {
      event.preventDefault()
      scrollToAnchor(anchor)
    }
  }
}
</script>

<style lang="scss" scoped>
// Sur la maquette le héro est quasi pleine largeur (2136 px sur un plan de travail
// de 2200), contrairement aux autres sections qui restent dans le conteneur de
// 1160 px. On reproduit ce cadrage : le slider grandit avec l'écran.
.hero {
  padding-top: 12px;
  // respiration avant la section suivante (21 px sur la maquette en 2136 de large,
  // donc ~1 % de la largeur : on garde la proportion quelle que soit la taille d'écran)
  padding-bottom: clamp(14px, 1vw, 22px);
  width: min(calc(100% - 44px), 2136px);
  max-width: none;
}

// proportion du visuel desktop livré (2141 x 778)
.hero__stage {
  position: relative;
  aspect-ratio: 2141 / 778;
  max-width: 100%;
}

.hero__slide {
  display: block;
  height: 100%;

  picture,
  img {
    display: block;
    width: 100%;
    height: 100%;
  }

  img {
    object-fit: cover;
    border-radius: 14px;
  }
}

// flèches maquette : cercle transparent, anneau + flèche blancs (picto SLIDER livré)
.hero__arrow {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  z-index: 2;
  width: 46px;
  height: 46px;
  padding: 0;
  border: none;
  background: none;
  transition: transform 0.2s ease, opacity 0.2s ease;

  img {
    width: 100%;
    height: 100%;
    display: block;
    filter: brightness(0) invert(1) drop-shadow(0 1px 4px rgba(#00264A, 0.35));
  }

  &:hover {
    transform: translateY(-50%) scale(1.08);
  }

  &.is-prev {
    left: 18px;

    img { transform: rotate(-90deg); }
  }

  &.is-next {
    right: 18px;

    img { transform: rotate(90deg); }
  }
}

.slidefade-enter-active,
.slidefade-leave-active {
  transition: opacity 0.35s ease;
}

.slidefade-enter-from,
.slidefade-leave-to {
  opacity: 0;
}

@include mobile {
  // le visuel mobile est portrait (1242 x 1982) et déjà cadré avec ses marges
  .hero {
    width: 100%;
    padding-top: 0;
  }

  .hero__stage {
    aspect-ratio: 1242 / 1982;
  }

  .hero__slide img {
    border-radius: 0;
  }

  .hero__arrow {
    width: 38px;
    height: 38px;
    // en mobile la photo est empilée au-dessus du panneau : ancrer les flèches sur la photo
    top: 22%;

    &.is-prev { left: 10px; }
    &.is-next { right: 10px; }
  }
}
</style>
